import React, { useState, useEffect } from 'react';
import {
  Popover,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  makeStyles,
} from '@material-ui/core';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';

const useStyles = makeStyles(() => ({
  paper: {
    overflow: 'visible', // Required for the arrow to peek outside the box
    marginTop: 8, // Adds a small gap between the chip and the popover
    borderRadius: 8,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  arrow: {
    position: 'absolute',
    top: -6, // Pulls the diamond up so half sits outside the box
    left: 24, // Aligns with the left-anchored chip
    width: 14,
    height: 14,
    backgroundColor: '#fff',
    transform: 'rotate(45deg)',
    boxShadow: '-3px -3px 5px -2px rgba(0,0,0,0.1)', // Casts shadow only on the top-left pointing edges
    zIndex: 0,
  },
  popoverContent: {
    position: 'relative',
    zIndex: 1, // Keeps content above the internal half of the arrow div
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    width: 300,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
}));

export default function StageChangePopover({
  anchorEl,
  open,
  onClose,
  contact,
  targetStage,
  onConfirm,
}) {
  const classes = useStyles();
  const [note, setNote] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');

  useEffect(() => {
    if (open) {
      setNote('');
      setFollowUpDate('');
    }
  }, [open]);

  if (!contact || !targetStage) return null;

  const handleUpdate = () => {
    onConfirm({
      contact,
      newStage: targetStage,
      note: note.trim(),
      followUpDate: followUpDate || null,
    });
    onClose();
  };

  // Prevents popover from closing when clicking outside
  const handlePopoverClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return; 
    }
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      classes={{ paper: classes.paper }} // Injects custom paper styles to allow overflow
      elevation={0} // Disabling default elevation since we apply custom box-shadow in classes.paper
    >
      <Box className={classes.popoverContent}>
        
        {/* Directional Arrow */}
        <div className={classes.arrow} />
        
        {/* Stage Transition Indicator */}
        <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={6}>
          <Box display="flex" alignItems="center" gridGap={6}>
            <Chip 
              label={contact.crmStageName || 'Unassigned'} 
              size="small" 
              style={{ height: 22, fontSize: '0.72rem', backgroundColor: '#eef1f0', color: '#5a6268' }} 
            />
            <ArrowRightAltIcon style={{ fontSize: 18, color: '#8a9296' }} />
            <Chip 
              label={targetStage.label} 
              size="small" 
              color="primary" 
              style={{ height: 22, fontSize: '0.72rem' }} 
            />
          </Box>
        </Box>

        <TextField
          label="Additional Note (Optional)"
          multiline
          rows={2}
          variant="outlined"
          size="small"
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add details about this update..."
        />

        <TextField
          label="Next Follow-up Date"
          type="date"
          variant="outlined"
          size="small"
          fullWidth
          value={followUpDate}
          onChange={(e) => setFollowUpDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Box display="flex" justifyContent="flex-end" gridGap={8} pt={0.5}>
          <Button size="small" onClick={onClose} style={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disableElevation
            onClick={handleUpdate}
            style={{ textTransform: 'none', fontWeight: 600 }}
          >
            Update Stage
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}